import React, { useState } from "react";
import {
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  continueAsGuest,
} from "../services/authService";
import { loginWithSpotify } from "../services/spotifyService";
import { useAuth } from "../context/AuthContext";

export default function AuthScreen({ onSuccess, onBack, initialError = "" }) {
  const { setGuestUser } = useAuth();
  const [tab, setTab] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState(initialError);
  const [loading, setLoading] = useState(false);

  const handleEmail = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (tab === "signup") {
        await signUpWithEmail(email, password, displayName);
      } else {
        await signInWithEmail(email, password);
      }
      onSuccess();
    } catch (err) {
      setError(err.message.replace("Firebase: ", "").replace(/\(auth.*\)/, "").trim());
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithGoogle();
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGuest = () => {
    const guest = continueAsGuest();
    setGuestUser(guest);
    onSuccess();
  };

  return (
    <div style={{
      background: "#0A0A0A", minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px", fontFamily: "'DM Sans', system-ui, sans-serif",
      position: "relative", overflow: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        .auth-glow { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(29,185,84,0.08) 0%, transparent 70%); pointer-events: none; }
        .auth-back { position: absolute; top: 24px; left: 24px; z-index: 2; display: flex; align-items: center; gap: 4px; color: rgba(255,255,255,0.4); font-size: 14px; cursor: pointer; background: none; border: none; font-family: 'DM Sans', sans-serif; transition: color 0.15s; }
        .auth-back:hover { color: rgba(255,255,255,0.7); }
        .auth-back:active { transform: scale(0.97); }
        .auth-card { width: 100%; max-width: 420px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 24px; padding: 36px 28px; position: relative; z-index: 1; }
        .auth-logo { font-family: 'DM Serif Display', serif; font-size: 42px; color: #fff; letter-spacing: -2px; text-align: center; margin-bottom: 8px; }
        .auth-sub { font-size: 14px; color: rgba(255,255,255,0.5); text-align: center; margin-bottom: 28px; line-height: 1.5; }
        .auth-tabs { display: flex; background: rgba(255,255,255,0.06); border-radius: 100px; padding: 4px; margin-bottom: 24px; }
        .auth-tab { flex: 1; padding: 10px 16px; border-radius: 100px; border: none; background: none; color: rgba(255,255,255,0.4); font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
        .auth-tab.active { background: #ffffff; color: #0A0A0A; }
        .auth-tab:active { transform: scale(0.97); }
        .auth-input { width: 100%; padding: 14px 16px; border-radius: 12px; background: rgba(255,255,255,0.06); border: 1.5px solid transparent; font-family: 'DM Sans', sans-serif; font-size: 15px; color: #fff; outline: none; margin-bottom: 12px; transition: all 0.15s; }
        .auth-input:focus { border-color: rgba(255,255,255,0.3); background: rgba(255,255,255,0.08); }
        .auth-input::placeholder { color: rgba(255,255,255,0.3); }
        .auth-btn { width: 100%; padding: 14px 32px; border-radius: 100px; border: none; font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; margin-bottom: 10px; display: flex; align-items: center; justify-content: center; gap: 10px; }
        .auth-btn:active { transform: scale(0.97); }
        .auth-btn-primary { background: #ffffff; color: #0A0A0A; font-weight: 700; }
        .auth-btn-primary:hover { background: rgba(255,255,255,0.9); transform: translateY(-1px); }
        .auth-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .auth-btn-google { background: none; color: #ffffff; border: 1.5px solid rgba(255,255,255,0.2); }
        .auth-btn-google:hover { border-color: rgba(255,255,255,0.5); background: rgba(255,255,255,0.05); }
        .auth-btn-spotify { background: #1DB954; color: #ffffff; border: none; font-weight: 700; }
        .auth-btn-spotify:hover { background: #1aa34a; }
        .auth-btn-guest { background: none; color: rgba(255,255,255,0.4); border: none; font-size: 13px; font-weight: 500; }
        .auth-btn-guest:hover { color: rgba(255,255,255,0.7); }
        .auth-divider { display: flex; align-items: center; gap: 12px; margin: 20px 0; }
        .auth-divider-line { flex: 1; height: 1px; background: rgba(255,255,255,0.08); }
        .auth-divider-text { font-size: 11px; color: rgba(255,255,255,0.3); font-weight: 600; white-space: nowrap; letter-spacing: 0.5px; text-transform: uppercase; }
        .auth-error { background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); border-radius: 12px; padding: 12px 14px; font-size: 13px; color: #ff6b6b; margin-bottom: 16px; line-height: 1.5; }
      `}</style>

      <div className="auth-glow" />
      <button className="auth-back" onClick={onBack}>‹ Back</button>

      <div className="auth-card">
        <div className="auth-logo">tapped</div>
        <div className="auth-sub">
          {tab === "signin" ? "Welcome back" : "Create your profile"}
        </div>

        <div className="auth-tabs">
          <button className={`auth-tab ${tab === "signin" ? "active" : ""}`} onClick={() => setTab("signin")}>Sign In</button>
          <button className={`auth-tab ${tab === "signup" ? "active" : ""}`} onClick={() => setTab("signup")}>Sign Up</button>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleEmail}>
          {tab === "signup" && (
            <input
              className="auth-input"
              placeholder="Display name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              required
            />
          )}
          <input
            className="auth-input"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <input
            className="auth-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
            {loading ? "..." : tab === "signin" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <div className="auth-divider">
          <div className="auth-divider-line" />
          <div className="auth-divider-text">or continue with</div>
          <div className="auth-divider-line" />
        </div>

        <button className="auth-btn auth-btn-google" onClick={handleGoogle} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>

        <button className="auth-btn auth-btn-spotify" onClick={() => loginWithSpotify("auth")} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#1DB954"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
          Continue with Spotify
        </button>

        <div className="auth-divider">
          <div className="auth-divider-line" />
          <div className="auth-divider-text">or</div>
          <div className="auth-divider-line" />
        </div>

        <button className="auth-btn auth-btn-guest" onClick={handleGuest}>
          Continue as Guest
        </button>

        <div style={{ marginTop: 20, fontSize: 11, color: "rgba(255,255,255,0.3)", textAlign: "center", lineHeight: 1.6 }}>
          By continuing you agree to our Terms of Service and Privacy Policy.
          Guest sessions don't save your history or preferences.
        </div>
      </div>
    </div>
  );
}
